export type ExperimentValue = string | number | boolean;
export type CampaignStatus = 'draft' | 'running' | 'paused' | 'completed';
export type UtmField = 'utm_source' | 'utm_medium' | 'utm_campaign' | 'utm_term' | 'utm_content';
export const utmFields: UtmField[] = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_term', 'utm_content'];

export interface Variant {
  id: string;
  weight: number;
  value: ExperimentValue;
}

export interface UtmConditions {
  utm_source?: string[];
  utm_medium?: string[];
  utm_campaign?: string[];
  utm_term?: string[];
  utm_content?: string[];
}

export interface Measurement {
  conversionEvent: string;
}

export interface Campaign {
  id: string;
  serviceId: string;
  environment: string;
  key: string;
  assignmentVersion: number;
  status: CampaignStatus;
  active?: boolean;
  trafficPercent: number;
  variants: Variant[];
  utmConditions?: UtmConditions | null;
  measurement?: Measurement | null;
  createdAt: string;
  updatedAt: string;
}

export interface CampaignInput {
  serviceId: string;
  environment: string;
  key: string;
  assignmentVersion?: number;
  trafficPercent: number;
  variants: Variant[];
  utmConditions?: UtmConditions | null;
  measurement?: Measurement;
}

export interface CampaignListResponse {
  campaigns: Campaign[];
  total: number;
  page: number;
  limit: number;
}

export interface Session {
  serviceId: string;
  environment: string;
}

export interface GroupResult {
  variantId: string;
  assignedVisitors: number;
  convertedVisitors: number;
  conversionRate: number | null;
}

export interface CampaignResults {
  campaignId: string;
  serviceId: string;
  environment: string;
  assignmentVersion: number;
  measurement?: Measurement | null;
  utmConditions?: UtmConditions | null;
  from: string;
  to: string;
  groups: GroupResult[];
  trafficPercent?: number;
  diagnostics: {
    unlinkedConversions: number;
    conflictingVisitors: number;
    lateEvents: number;
  };
  generatedAt?: string;
}

export class ApiError extends Error {
  readonly status: number;
  readonly code: string;

  constructor(status: number, code: string, message = code) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.code = code;
  }
}

export function apiUrl(path: string): string {
  const base = (import.meta.env.VITE_ABTOOL_API_URL as string | undefined)?.replace(/\/$/, '') ?? '';
  return `${base}${path.startsWith('/') ? path : `/${path}`}`;
}

export async function rawFetch(path: string, options: RequestInit = {}): Promise<Response> {
  return fetch(apiUrl(path), {
    ...options,
    credentials: 'include',
    headers: {
      Accept: 'application/json',
      ...(options.body ? { 'Content-Type': 'application/json' } : {}),
      ...options.headers,
    },
  });
}

export async function apiFetch<T>(path: string, options: RequestInit = {}): Promise<T> {
  const response = await rawFetch(path, options);
  const text = await response.text();
  let body: unknown = undefined;
  if (text) {
    try { body = JSON.parse(text); } catch { body = undefined; }
  }
  if (!response.ok) {
    const record = body && typeof body === 'object' ? body as Record<string, unknown> : {};
    const code = typeof record.error === 'string' ? record.error : `HTTP_${response.status}`;
    throw new ApiError(response.status, code, code);
  }
  return body as T;
}

export const adminApi = {
  session: () => apiFetch<Session>('/api/admin-session'),
  login: (input: { token: string; serviceId: string; environment: string }) => apiFetch<Session>('/api/admin-session', {
    method: 'POST', body: JSON.stringify(input),
  }),
  logout: () => apiFetch<void>('/api/admin-session', { method: 'DELETE' }),
  listCampaigns: (params: { serviceId: string; environment: string; status?: CampaignStatus | ''; page?: number; limit?: number }) => {
    const query = new URLSearchParams({ serviceId: params.serviceId, environment: params.environment });
    if (params.status) query.set('status', params.status);
    query.set('page', String(params.page ?? 1));
    query.set('limit', String(params.limit ?? 20));
    return apiFetch<CampaignListResponse>(`/api/admin/campaigns?${query.toString()}`);
  },
  getCampaign: (id: string, context: Session) => apiFetch<Campaign>(`/api/admin/campaigns/${encodeURIComponent(id)}?serviceId=${encodeURIComponent(context.serviceId)}&environment=${encodeURIComponent(context.environment)}`),
  createCampaign: (input: CampaignInput) => apiFetch<Campaign>('/api/admin/campaigns', { method: 'POST', body: JSON.stringify(input) }),
  updateCampaign: (id: string, input: CampaignInput) => apiFetch<Campaign>(`/api/admin/campaigns/${encodeURIComponent(id)}`, { method: 'PATCH', body: JSON.stringify(input) }),
  transition: (id: string, input: Session & { status: CampaignStatus }) => apiFetch<Campaign>(`/api/admin/campaigns/${encodeURIComponent(id)}/transition`, { method: 'POST', body: JSON.stringify(input) }),
  results: (id: string, params: Session & { from?: string; to?: string }) => {
    const query = new URLSearchParams({ serviceId: params.serviceId, environment: params.environment });
    if (params.from) query.set('from', params.from);
    if (params.to) query.set('to', params.to);
    return apiFetch<CampaignResults>(`/api/admin/campaigns/${encodeURIComponent(id)}/results?${query.toString()}`);
  },
};
